import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../campaigns.css';
import { CampaignService, CampaignTypeService } from '../../../../../Services/Axios/Api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MediaSelector from '../../../Media/MediaSelector';

const AddCampaign = () => {
    const [formData, setFormData] = useState({
        name: '',
        type: '',
        description: '',
        target: 0,
        mainPhoto: '',
        mediaFiles: [],
        isRecurring: false,
        recurringMode: 'nextMonth', // 'nextMonth' or 'calendarMonth'
        recurringAmount: 0, 
        isFixedPrice: false,
        fixedPrice: 0,
        showQr: false,
        isVisible: true,
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [selectedTypeMode, setSelectedTypeMode] = useState('');
    const [selectedTypeIsGeneral, setSelectedTypeIsGeneral] = useState(false);
    const [campaignTypes, setCampaignTypes] = useState([]);
    const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
    const [isAdditionalMediaSelectorOpen, setIsAdditionalMediaSelectorOpen] = useState(false);
    const [currentMediaSelectorType, setCurrentMediaSelectorType] = useState('main'); // 'main' or 'additional'

    const navigate = useNavigate();

    useEffect(() => {
        fetchCampaignTypes();
    }, []);

    const fetchCampaignTypes = async () => {
        try {
            const response = await CampaignTypeService.getCampaignTypes();
            setCampaignTypes(response.result);
        } catch (error) {
            toast.error('Failed to fetch campaign types');
            console.error('Error fetching campaign types:', error);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.type) {
            newErrors.type = 'Type is required';
        }
        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }
        if (selectedTypeMode === 'Progressing' && !formData.target) {
            newErrors.target = 'Target is required for progressive campaigns';
        }
        if (!formData.mainPhoto) {
            newErrors.mainPhoto = 'Main photo is required';
        }
        if (formData.isRecurring && !formData.recurringMode) {
            newErrors.recurringMode = 'Please select a recurring mode';
        }
        if (formData.isFixedPrice && (!formData.fixedPrice || formData.fixedPrice <= 0)) {
            newErrors.fixedPrice = 'Valid fixed price is required';
        }
        return newErrors;
    };

    const handleTypeChange = (e) => {
        const selectedType = campaignTypes.find(type => type.id == e.target.value);
        setSelectedTypeMode(selectedType ? selectedType.mode : '');
        setSelectedTypeIsGeneral(selectedType ? selectedType.isGeneralDonation : false);
        setFormData({
            ...formData,
            type: e.target.value,
            target: selectedType?.mode === 'Progressing' ? formData.target : '',
            isRecurring: formData.isRecurring
        });
    };

    const handleMediaSelect = (selectedPath) => {
        if (currentMediaSelectorType === 'main') {
            // For main media, single selection is expected
            const main = Array.isArray(selectedPath) ? selectedPath[0] : selectedPath;
            setFormData({ ...formData, mainPhoto: main });
        } else {
            // For additional media, append to existing selections without resetting
            const newItems = Array.isArray(selectedPath) ? selectedPath : [selectedPath];
            setFormData(prev => {
                const combined = [...prev.mediaFiles, ...newItems];
                // Deduplicate while preserving order
                const seen = new Set();
                const deduped = combined.filter((item) => {
                    if (seen.has(item)) return false;
                    seen.add(item);
                    return true;
                });
                return { ...prev, mediaFiles: deduped };
            });
        }
    };

    const handleRemoveAdditional = (index) => {
        setFormData(prev => ({
            ...prev,
            mediaFiles: prev.mediaFiles.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validateForm();
        if (Object.keys(formErrors).length === 0) {
            try {
                setLoading(true);
                setErrors({});
                await CampaignService.createCampaign({
                    name: formData.name,
                    campaignSectionId: formData.type,
                    description: formData.description,
                    targetAmount: formData.target||0,
                    mainImage: formData.mainPhoto,
                    additionalImages: formData.mediaFiles.join(','),
                    options: JSON.stringify({ showQr: !!formData.showQr }),
                    isRecurring: formData.isRecurring,
                    recurringMode: formData.recurringMode,
                    recurringAmount: formData.isFixedPrice ? formData.fixedPrice : 0,
                    isFixedPrice: formData.isFixedPrice,
                    fixedPrice: formData.fixedPrice || 0,
                    isVisible: formData.isVisible
                });
                setShowSuccess(true);
                toast.success('Campaign created successfully');
                setTimeout(() => {
                    navigate('/bj-admin/donations/campaigns');
                }, 2000);
            } catch (error) {
                toast.error('Failed to create campaign');
                console.error('Error creating campaign:', error);
            } finally {
                setLoading(false);
            }
        } else {
            setErrors(formErrors);
        }
    };

    const handleBack = () => {
        navigate('/bj-admin/donations/campaigns');
    };

    // Utility function to check for video file extensions
    const isVideoFile = (filePath) => {
        return /\.(mp4|mov|webm|avi|mkv|m4v|ogg|wmv)$/i.test(filePath);
    };

    // Utility function to check for image file extensions
    const isImageFile = (filePath) => {
        return /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|tiff|tif)$/i.test(filePath);
    };

    return (
        <Container className="mt-5">
            <h2 className="mb-4">New Campaign</h2>
            {showSuccess && (
                <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                    Campaign added successfully!
                </Alert>
            )}
            <Form onSubmit={handleSubmit} noValidate>
                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formName">
                            <Form.Label>Campaign Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter campaign name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                isInvalid={!!errors.name}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.name}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formType">
                            <Form.Label>Campaign Type</Form.Label>
                            <Form.Select
                                value={formData.type}
                                onChange={handleTypeChange}
                                isInvalid={!!errors.type}
                            >
                                <option value="">Select campaign type</option>
                                {campaignTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {errors.type}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                {selectedTypeMode === 'Progressing' && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="formTarget">
                                <Form.Label>Target Amount</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter target amount"
                                    value={formData.target}
                                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                                    isInvalid={!!errors.target}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.target}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formDescription">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter campaign description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                isInvalid={!!errors.description}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.description}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formMainPhoto">
                            <Form.Label>Main Photo</Form.Label>
                            <div className="d-flex align-items-center">
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => {
                                        setCurrentMediaSelectorType('main');
                                        setIsMediaSelectorOpen(true);
                                    }}
                                >
                                    Select Main Photo
                                </Button>
                                {formData.mainPhoto && (
                                    <div className="ms-3">
                                        <div className="media-thumb">
                                            {isVideoFile(formData.mainPhoto) ? (
                                                <video 
                                                    src={formData.mainPhoto} 
                                                    className="media-thumb__media"
                                                    controls 
                                                />
                                            ) : isImageFile(formData.mainPhoto) ? (
                                                <img 
                                                    src={formData.mainPhoto} 
                                                    alt="Main campaign" 
                                                    className="media-thumb__media"
                                                />
                                            ) : (
                                                <iframe 
                                                    src={formData.mainPhoto}
                                                    className="media-thumb__media"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                    allowFullScreen
                                                    loading="lazy"
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                    title="main-media-preview"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {errors.mainPhoto && (
                                <div className="text-danger mt-1">{errors.mainPhoto}</div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formMediaFiles">
                            <Form.Label>Additional Photos/Videos</Form.Label>
                            <div className="d-flex align-items-center">
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => {
                                        setCurrentMediaSelectorType('additional');
                                        setIsAdditionalMediaSelectorOpen(true);
                                    }}
                                >
                                    Select Additional Media
                                </Button>
                                {formData.mediaFiles.length > 0 && (
                                    <div className="ms-3 media-thumb-grid">
                                        {formData.mediaFiles.map((file, index) => (
                                            <div key={index} className="media-thumb">
                                                {isVideoFile(file) ? (
                                                    <video
                                                        src={file}
                                                        className="media-thumb__media"
                                                        controls
                                                    />
                                                ) : isImageFile(file) ? (
                                                    <img 
                                                        src={file} 
                                                        alt={`Additional ${index + 1}`} 
                                                        className="media-thumb__media"
                                                    />
                                                ) : (
                                                    <iframe 
                                                        src={file}
                                                        className="media-thumb__media"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                        allowFullScreen
                                                        loading="lazy"
                                                        referrerPolicy="strict-origin-when-cross-origin"
                                                        title={`additional-media-${index}`}
                                                    />
                                                )}
                                                <button 
                                                    type="button"
                                                    className="media-thumb__remove"
                                                    onClick={() => handleRemoveAdditional(index)}
                                                    aria-label={`Remove additional media ${index + 1}`}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                
                {!selectedTypeIsGeneral && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="formShowQr" className="mb-2">
                                <Form.Check
                                    type="switch"
                                    id="showQrSwitch"
                                    label="Show Quick Donate QR on campaign page"
                                    checked={!!formData.showQr}
                                    onChange={e => setFormData({ ...formData, showQr: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formFixedPriceToggle" className="mb-2">
                                <Form.Check
                                    type="switch"
                                    id="fixedPriceSwitch"
                                    label="Fixed Price Campaign (سعر ثابت)"
                                    checked={formData.isFixedPrice}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData({ 
                                            ...formData, 
                                            isFixedPrice: checked
                                        });
                                    }}
                                />
                            </Form.Group>
                            <Form.Group controlId="formRecurring">
                                <Form.Check
                                    type="checkbox"
                                    label="Make this a recurring campaign"
                                    checked={formData.isRecurring}
                                    onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formIsVisible" className="mt-2">
                                <Form.Check
                                    type="switch"
                                    id="isVisibleSwitch"
                                    label="Show on Website (visible on Home & Campaigns pages)"
                                    checked={formData.isVisible}
                                    onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}
                {selectedTypeIsGeneral && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="formShowQr" className="mb-2">
                                <Form.Check
                                    type="switch"
                                    id="showQrSwitchGeneral"
                                    label="Show Quick Donate QR on campaign page"
                                    checked={!!formData.showQr}
                                    onChange={e => setFormData({ ...formData, showQr: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formFixedPriceToggleGeneral" className="mb-2">
                                <Form.Check
                                    type="switch"
                                    id="fixedPriceSwitchGeneral"
                                    label="Fixed Price Campaign (سعر ثابت)"
                                    checked={formData.isFixedPrice}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData({ 
                                            ...formData, 
                                            isFixedPrice: checked
                                        });
                                    }}
                                />
                            </Form.Group>
                            <Form.Group controlId="formRecurringGeneral">
                                <Form.Check
                                    type="checkbox"
                                    label="Make this a recurring campaign"
                                    checked={formData.isRecurring}
                                    onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formIsVisibleGeneral" className="mt-2">
                                <Form.Check
                                    type="switch"
                                    id="isVisibleSwitchGeneral"
                                    label="Show on Website (visible on Home & Campaigns pages)"
                                    checked={formData.isVisible}
                                    onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {formData.isFixedPrice && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="formFixedPrice">
                                <Form.Label>Fixed Price per Item (سعر ثابت)</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter fixed price"
                                    value={formData.fixedPrice}
                                    onChange={(e) => setFormData({ ...formData, fixedPrice: e.target.value })}
                                    isInvalid={!!errors.fixedPrice}
                                    min="1"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.fixedPrice}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {formData.isRecurring && (
                    <>
                        <Row className="mb-3">
                            <Col>
                                <Form.Group controlId="formRecurringMode">
                                    <Form.Label>Recurring Mode</Form.Label>
                                    <div className="border rounded p-3">
                                        <Form.Check
                                            type="radio"
                                            id="nextMonth"
                                            name="recurringMode"
                                            label={
                                                <div>
                                                    <strong>Next Month Mode</strong>
                                                    <p className="text-muted mb-0">
                                                        Subscription will be collected after one month from the subscription date.
                                                        Example: If someone subscribes on February 15th, their next payment will be on March 15th.
                                                    </p>
                                                </div>
                                            }
                                            checked={formData.recurringMode === 'nextMonth'}
                                            onChange={() => setFormData({ ...formData, recurringMode: 'nextMonth' })}
                                            className="mb-3"
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="calendarMonth"
                                            name="recurringMode"
                                            label={
                                                <div>
                                                    <strong>Calendar Month Mode</strong>
                                                    <p className="text-muted mb-0">
                                                        For subscriptions between 1-15: Payment is for current month, next billing on 1st of next month.
                                                        For subscriptions between 16-31: Payment is for next month, next billing on 1st of following month.
                                                        Example: If someone subscribes on February 13th, they pay for February and next billing is March 1st.
                                                        If they subscribe on February 20th, they pay for March and next billing is April 1st.
                                                    </p>
                                                </div>
                                            }
                                            checked={formData.recurringMode === 'calendarMonth'}
                                            onChange={() => setFormData({ ...formData, recurringMode: 'calendarMonth' })}
                                        />
                                    </div>
                                    {errors.recurringMode && (
                                        <div className="text-danger mt-1">{errors.recurringMode}</div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}

                <div className="d-flex justify-content-end">
                    <Button variant="success" className="me-2" type="submit" disabled={loading}>
                        {loading ? (
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                    <Button variant="danger" onClick={handleBack}>
                        Cancel
                    </Button>
                </div>
            </Form>

            <MediaSelector
                isOpen={isMediaSelectorOpen}
                onClose={() => setIsMediaSelectorOpen(false)}
                onSelect={handleMediaSelect}
                isMultiple={false}
            />

            <MediaSelector
                isOpen={isAdditionalMediaSelectorOpen}
                onClose={() => setIsAdditionalMediaSelectorOpen(false)}
                onSelect={handleMediaSelect}
                isMultiple={true}
            />
        </Container>
    );
};

export default AddCampaign; 